<script>
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';
import format from 'date-fns/format';
import ButtonNext from 'next/button/Button.vue';
import IconNext from 'next/icon/Icon.vue';
import InputNext from 'next/input/Input.vue';
import DatePicker from 'vue-datepicker-next';
import VariablesDropdown from '../variablesDropdown/Index.vue';
import variablesMixins from '../variablesDropdown/variablesMixins';
import { containsVariable } from './helpers/validations';

export default {
  components: {
    DatePicker,
    VariablesDropdown,
    ButtonNext,
    IconNext,
    InputNext,
  },
  mixins: [variablesMixins],
  props: {
    value: {
      type: [String, Number, Date, Boolean, Object, Array],
      default: undefined,
    },
    defaultValue: {
      type: [String, Number, Date, Boolean, Object, Array],
      default: undefined,
    },
    type: {
      type: String,
      default: 'date',
    },
    label: {
      type: String,
      default: '',
    },
    fallbackLabel: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      required: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dirty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['dropdownClose', 'dropdownOpen', 'change'],
  setup() {
    const { toLocale } = useAutomationLocales();

    return {
      v$: useVuelidate(),
      toLocale,
    };
  },
  data() {
    return {
      showDateDropdown: false,
      updatedValue: this.value ?? this.defaultValue,
    };
  },
  validations() {
    return {
      updatedValue: {
        required: requiredIf(this.required),
      },
    };
  },
  computed: {
    datepickerConfirm() {
      return this.type === 'time' || this.type === 'datetime';
    },
    datepickerFormat() {
      if (this.type === 'date') {
        return 'YYYY-MM-DD';
      }
      if (this.type === 'time') {
        return 'HH:mm';
      }
      if (this.type === 'datetime') {
        return 'YYYY-MM-DD HH:mm:ss';
      }
      return undefined;
    },
    descriptionTooltip() {
      if (!this.description) return null;

      return {
        content: this.toLocale(this.description),
        popperClass:
          'max-w-[300px] max-h-[300px] overflow-y-auto whitespace-pre-line',
        boundary: 'body',
        popperTriggers: ['hover'],
      };
    },
  },
  watch: {
    value(value) {
      if (containsVariable(value)) {
        this.updatedValue = value;
      } else if (this.type === 'datetime') {
        this.updatedValue = value
          ? format(new Date(value), 'yyyy-MM-dd HH:mm:ss')
          : '';
      }
    },
  },
  mounted() {
    if (this.dirty) {
      this.$nextTick(() => {
        this.v$.$validate();
      });
    }
  },
  methods: {
    onDateChange(value) {
      this.updatedValue = value;
      this.$emit(
        'change',
        this.type === 'datetime' ? new Date(value).toISOString() : value
      );
      if (this.type === 'date') {
        this.hideDateDropdown();
      }
    },
    onVariableAdd(_, variableName, prefix) {
      const updatedValue = `${prefix}{${variableName}}`;
      this.updatedValue = updatedValue;
      this.$emit('change', updatedValue);
      this.hideDateDropdown();
    },
    openDateDropdown() {
      this.showDateDropdown = true;
      this.$emit('dropdownOpen');
    },
    hideDateDropdown() {
      this.showDateDropdown = false;
      this.$emit('dropdownClose');
    },
  },
};
</script>

<template>
  <div>
    <div
      class="relative flex flex-col min-w-0 gap-1"
      :class="{ error: v$.updatedValue.$error }"
    >
      <label
        v-if="label || descriptionTooltip"
        class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
      >
        <div v-if="label" class="block truncate">
          {{ toLocale(label, fallbackLabel) }}<span v-if="required">*</span>
        </div>
        <IconNext
          v-if="descriptionTooltip"
          v-tooltip.top="descriptionTooltip"
          icon="i-lucide-info"
          class="text-n-slate-11 size-3.5"
        />
      </label>

      <DatePicker
        class="date-picker"
        :type="type"
        :confirm="datepickerConfirm"
        :format="datepickerFormat"
        :clearable="false"
        :editable="false"
        :open="showDateDropdown"
        :value="updatedValue"
        :append-to-body="false"
        :disabled="disabled"
        :placeholder="placeholder"
        value-type="format"
        @change="onDateChange"
        @open="openDateDropdown"
        @confirm="hideDateDropdown"
      >
        <template #input>
          <!-- <input
            v-model="updatedValue"
            type="text"
            readonly
            class="cursor-pointer"
          /> -->
          <InputNext
            v-model="updatedValue"
            type="text"
            readonly
            class="cursor-pointer"
          />
        </template>
        <template #footer>
          <div>
            <ButtonNext
              v-if="showVariablesSelector"
              icon="i-lucide-braces"
              color="slate"
              size="xs"
              variant="faded"
              @click="openVariablesDropdown"
            />
            <div
              class="modal-mask z-[-1] !bg-transparent cursor-pointer"
              @click="hideDateDropdown"
            />
          </div>
        </template>
      </DatePicker>

      <span v-if="v$.updatedValue.$error" class="message">
        <template v-if="v$.updatedValue.required.$invalid">{{
          $t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.REQUIRED')
        }}</template>
        <template v-else>{{
          $t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.INVALID')
        }}</template>
      </span>
    </div>

    <VariablesDropdown
      :show-dropdown="showVariablesDropdown"
      :event="variablesDropdownEvent"
      :variables="variables"
      :filter-by-prefix="variablesFilter"
      @close="closeVariablesDropdown"
      @add="addVariable"
    />
  </div>
</template>

<style lang="scss" scoped>
.date-picker {
  @apply w-full z-40;
  ::v-deep {
    input[name='date'] {
      @apply rounded-md dark:bg-slate-900 dark:text-slate-200 text-sm border border-slate-500 p-2 pe-7 truncate;
    }
    .mx-datepicker-footer {
      @apply flex justify-between items-center;
    }
  }
}
[dir='rtl'] .date-picker {
  ::v-deep {
    .mx-icon-calendar {
      right: unset;
      left: 8px;
    }
  }
}
.error input {
  @apply border-red-500;
}

:is(.dark .error input) {
  @apply border-red-500;
}
</style>
